import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader, getUser } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, Eye, Edit, UserCheck, UserX, CheckCircle, XCircle } from 'lucide-react';

const ALLOWED_ROLES = ['admin', 'finance_head', 'finance_team', 'function_approver'];
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

const EntityManagement = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', gst_no: '' });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    if (!user || !ALLOWED_ROLES.includes(user.role)) {
      toast.error('Not authorized to access entities');
      navigate('/');
      return;
    }
    fetchEntities();
  }, []);

  const fetchEntities = async () => {
    try {
      const response = await axios.get(`${API}/entities`, { headers: getAuthHeader() });
      setEntities(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch entities');
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return false;
    }
    if (!formData.address.trim()) {
      toast.error('Address is required');
      return false;
    }
    if (!formData.gst_no.trim()) {
      return true;
    }
    const normalizedGst = formData.gst_no.trim().toUpperCase();
    if (!GSTIN_PATTERN.test(normalizedGst)) {
      toast.error('Invalid GST number format');
      return false;
    }
    return true;
  };

  const getGstPayloadValue = () => {
    const gst = formData.gst_no.trim();
    return gst ? gst.toUpperCase() : null;
  };

  const handleCreateEntity = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        gst_no: getGstPayloadValue(),
      };
      await axios.post(`${API}/entities`, payload, { headers: getAuthHeader() });
      toast.success('Entity created successfully');
      setDialogOpen(false);
      resetForm();
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entity');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateEntity = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const payload = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        gst_no: getGstPayloadValue(),
      };
      await axios.put(`${API}/entities/${selectedEntity.id}`, payload, { headers: getAuthHeader() });
      toast.success('Entity updated successfully');
      setEditDialogOpen(false);
      resetForm();
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entity');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateEntity = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/entities/${selectedEntity.id}/deactivate`, {}, { headers: getAuthHeader() });
      toast.success('Entity deactivated successfully');
      setDeactivateDialogOpen(false);
      setSelectedEntity(null);
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deactivate entity');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateEntity = async (entityId) => {
    setLoading(true);
    try {
      await axios.put(`${API}/entities/${entityId}/activate`, {}, { headers: getAuthHeader() });
      toast.success('Entity activated successfully');
      fetchEntities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to activate entity');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', gst_no: '' });
    setSelectedEntity(null);
  };

  const openViewDialog = (entity) => {
    setSelectedEntity(entity);
    setViewDialogOpen(true);
  };

  const openEditDialog = (entity) => {
    setSelectedEntity(entity);
    setFormData({
      name: entity.name,
      address: entity.address,
      gst_no: entity.gst_no || '',
    });
    setEditDialogOpen(true);
  };

  const openDeactivateDialog = (entity) => {
    setSelectedEntity(entity);
    setDeactivateDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getFilteredEntities = () => {
    let filtered = [...entities];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (entity) =>
          entity.name?.toLowerCase().includes(term) ||
          entity.address?.toLowerCase().includes(term) ||
          entity.gst_no?.toLowerCase().includes(term) ||
          entity.created_by?.toLowerCase().includes(term)
      );
    }

    if (statusFilter === 'active') {
      filtered = filtered.filter((entity) => entity.is_active !== false);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter((entity) => entity.is_active === false);
    }

    return filtered;
  };

  const filteredEntities = getFilteredEntities();
  const totalPages = Math.ceil(filteredEntities.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentEntities = filteredEntities.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
            Entity Management
          </h1>
          <p className="text-gray-600 mt-1">Manage company entities and GST details</p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          className="bg-indigo-600 hover:bg-indigo-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Entity
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Entities ({entities.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-3 items-center flex-1">
              <input
                type="text"
                placeholder="Search by name, address, GST..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 max-w-md"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900 whitespace-normal break-words max-w-[180px]">Name</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900 whitespace-normal break-words max-w-[220px]">Address</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900 whitespace-normal break-words max-w-[140px]">GST No</th>
                  <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900 whitespace-normal break-words max-w-[100px]">Status</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900 whitespace-normal break-words max-w-[140px]">Updated By</th>
                  <th className="px-3 py-3 text-left text-sm font-semibold text-gray-900 whitespace-normal break-words max-w-[140px]">Updated At</th>
                  <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900 whitespace-normal break-words">View</th>
                  <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900 whitespace-normal break-words">Edit</th>
                  <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900 whitespace-normal break-words max-w-[120px]">Active / Inactive</th>
                </tr>
              </thead>
              <tbody>
                {currentEntities.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-3 py-8 text-center text-gray-500">
                      No entities found
                    </td>
                  </tr>
                ) : (
                  currentEntities.map((entity) => (
                    <tr key={entity.id} className={entity.is_active === false ? 'bg-red-50 border-b' : 'border-b hover:bg-gray-50'}>
                      <td className="px-3 py-3 text-sm text-gray-900 whitespace-normal break-words max-w-[180px]">
                        {entity.name}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 whitespace-normal break-words max-w-[220px]">
                        {entity.address}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900 whitespace-normal break-words max-w-[140px]">{entity.gst_no || 'N/A'}</td>
                      <td className="px-3 py-3 text-center whitespace-normal break-words">
                        {entity.is_active !== false ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Inactive</Badge>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 whitespace-normal break-words max-w-[140px]">{entity.updated_by || '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-600 whitespace-normal break-words max-w-[140px]">{formatDate(entity.updated_at)}</td>
                      <td className="px-3 py-3 text-center">
                        <Button variant="ghost" size="sm" onClick={() => openViewDialog(entity)} title="View">
                          <Eye className="w-4 h-4 text-blue-600" />
                        </Button>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(entity)} title="Edit">
                          <Edit className="w-4 h-4 text-indigo-600" />
                        </Button>
                      </td>
                      <td className="px-3 py-3 text-center">
                        {entity.is_active !== false ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeactivateDialog(entity)}
                            title="Deactivate"
                          >
                            <UserX className="w-4 h-4 text-red-600" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleActivateEntity(entity.id)}
                            title="Activate"
                            disabled={loading}
                          >
                            <UserCheck className="w-4 h-4 text-green-600" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredEntities.length)} of {filteredEntities.length} entities
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="px-3 py-1 text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Entity Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Entity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEntity} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Entity legal name"
                required
              />
            </div>
            <div>
              <Label htmlFor="address">Address *</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Registered address"
                rows={4}
                required
              />
            </div>
            <div>
              <Label htmlFor="gst_no">GST No</Label>
              <Input
                id="gst_no"
                value={formData.gst_no}
                onChange={(e) => setFormData({ ...formData, gst_no: e.target.value.toUpperCase() })}
                placeholder="e.g. 27AAJCA1582B1ZY"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                {loading ? 'Creating...' : 'Create Entity'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Entity Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Entity</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateEntity} className="space-y-4">
            <div>
              <Label htmlFor="edit_name">Name *</Label>
              <Input
                id="edit_name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_address">Address *</Label>
              <Textarea
                id="edit_address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                rows={4}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit_gst_no">GST No</Label>
              <Input
                id="edit_gst_no"
                value={formData.gst_no}
                onChange={(e) => setFormData({ ...formData, gst_no: e.target.value.toUpperCase() })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm text-gray-500">
              <div>
                <Label className="text-gray-500">Created By</Label>
                <p>{selectedEntity?.created_by || '-'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Created At</Label>
                <p>{formatDate(selectedEntity?.created_at)}</p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-indigo-600 hover:bg-indigo-700">
                {loading ? 'Updating...' : 'Update Entity'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Entity Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Entity Details</DialogTitle>
          </DialogHeader>
          {selectedEntity && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-500">Name</Label>
                <p className="text-sm font-medium">{selectedEntity.name}</p>
              </div>
              <div>
                <Label className="text-gray-500">Address</Label>
                <p className="text-sm whitespace-pre-wrap">{selectedEntity.address}</p>
              </div>
              <div>
                <Label className="text-gray-500">GST No</Label>
                <p className="text-sm">{selectedEntity.gst_no || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-gray-500">Status</Label>
                <div className="mt-1">
                  {selectedEntity.is_active !== false ? (
                    <Badge className="bg-green-100 text-green-800 flex items-center gap-1 w-fit">
                      <CheckCircle className="w-3 h-3" /> Active
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-800 flex items-center gap-1 w-fit">
                      <XCircle className="w-3 h-3" /> Inactive
                    </Badge>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Created By</Label>
                  <p className="text-sm">{selectedEntity.created_by || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Updated By</Label>
                  <p className="text-sm">{selectedEntity.updated_by || '-'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Created At</Label>
                  <p className="text-sm">{formatDate(selectedEntity.created_at)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Updated At</Label>
                  <p className="text-sm">{formatDate(selectedEntity.updated_at)}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate Confirmation Dialog */}
      <Dialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Entity</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate {selectedEntity?.name}? This entity will be marked as inactive.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeactivateEntity} disabled={loading}>
              {loading ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EntityManagement;
